/**
 * Theme
 * https://material-ui.com/customization
 */

import { createTheme } from '@mui/material/styles';
import { TypographyOptions } from '@mui/material/styles/createTypography';

export const palette = {
  primary: {
    main: '#ff7d6b',
    contrastText: '#fff',
  },
  secondary: {
    main: '#32db64',
  },
  error: {
    main: '#f53d3d',
  },
  warning: {
    main: '#FDC20F',
  },
  info: {
    main: '#2582f3',
  },
  success: {
    main: '#32db64',
  },
  text: {
    primary: '#000',
    secondary: '#ff7d6b',
    disabled: '#a7a7a7',
  },
  outlinePrimary: {
    main: '#838791',
  },
  colorGray: '#606060',
  colorGray3: '#838791',
  colorGray80: '#50545e',
  colorGray100: '#171413',
  colorGray0: '#f2f2f3',
  colorGrayLight: '#a7a7a7',
  colorGrayLighter: '#dedede',
  colorGrayLightest: '#f7f7f7',
  colorHighlight: '#f7f3f3',
};

const button = {
  borderWidth: 2,
  fontSize: '1.4rem',
  '&:hover': {
    borderWidth: 2,
  },
  boxShadow: 'none',
};

const outlinedButton = {
  borderColor: palette.colorGrayLighter,
  borderWidth: '1px !important',
  color: palette.colorGray,

  '&:hover': {
    backgroundColor: palette.colorGrayLightest,
    borderColor: palette.colorGrayLighter,
  },
};

const buttonSizeLarge = {
  paddingTop: 14,
  paddingBottom: 14,
};

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 960,
    lg: 1280,
    xl: 1920,
  },
};

// const smallScreenMediaQuery = `@media (max-width: ${breakpoints.values.md}px)`;

const fontFamily = ['Roboto', 'sans-serif', 'serif'].join(',');

const fontSizeDefault = {
  fontSize: '1.6rem',
};

const bodyMain = {
  paddingLeft: '16px',
  paddingRight: '16px',
  fontFamily,
  ...fontSizeDefault,
};

const typography: TypographyOptions = {
  fontFamily,
  htmlFontSize: 10,
  body1: {
    ...bodyMain,
    color: palette.text.primary,
  },
  button: {
    fontSize: '1.4rem',
    textTransform: 'none',
    fontWeight: 500,
    WebkitFontSmoothing: 'antialiased',
    boxShadow: 'none !important',
  },
  h1: {
    fontSize: '2.6rem',
    fontWeight: 400,
  },
  h2: {
    fontSize: '2.4rem',
    fontWeight: 400,
  },
  h3: {
    fontSize: '2.2rem',
    fontWeight: 400,
  },
  h4: {
    fontSize: '2rem',
    fontWeight: 400,
  },
  h5: {
    fontSize: '1.8rem',
    fontWeight: 500,
  },
  h6: {
    fontSize: '1.6rem',
    fontWeight: 500,
  },
};

const defaultTheme = createTheme({
  palette,
  breakpoints,
  typography,
  components: {
    // Dialog.
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          ...bodyMain,
          ...typography.h4,
        },
      },
    },

    MuiDialogContentText: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          color: palette.text.primary,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          ...bodyMain,
          paddingTop: 16,
          paddingBottom: 16,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          ...bodyMain,
          paddingTop: 0,
          paddingBottom: 16,
        },
      },
    },

    // List
    MuiListItem: {
      styleOverrides: {
        root: {
          ...fontSizeDefault,
          fontFamily,
          paddingTop: 4,
          paddingBottom: 4,
          '&$selected': {
            fontWeight: 500,
          },
          '&.placeholder': {
            display: 'none',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          padding: 0,
          '&.bold span': {
            fontWeight: 'bold',
          },
        },
        primary: {
          padding: 0,
          fontWeight: 300,
        },
        secondary: {
          padding: 0,
          fontWeight: 300,
        },
        dense: {
          padding: 0,
          fontWeight: 300,
        },
        inset: {
          padding: 0,
          fontWeight: 300,
        },
        multiline: {
          padding: 0,
          fontWeight: 300,
        },
      },
    },

    // Button
    MuiButton: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
        sizeLarge: {
          ...button,
          ...buttonSizeLarge,
        },
        containedSizeLarge: {
          ...button,
          ...buttonSizeLarge,
        },
        outlinedSizeLarge: {
          ...button,
          ...outlinedButton,
          ...buttonSizeLarge,
        },
        textSizeLarge: {
          ...button,
          ...buttonSizeLarge,
        },

        sizeSmall: {
          ...button,
        },
        containedSizeSmall: {
          ...button,
        },
        outlinedSizeSmall: {
          ...button,
          ...outlinedButton,
        },
        textSizeSmall: {
          ...button,
        },

        outlinedPrimary: {
          ...button,
          ...outlinedButton,
        },
        outlinedSecondary: {
          ...button,
        },
      },
    },

    // Icon button
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: '8px',
        },
        edgeStart: {
          marginLeft: '-12px',
        },
        edgeEnd: {
          marginRight: '-12px',
        },
      },
    },

    // Tabs
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: '40px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          position: 'relative',
          top: 1,
          borderBottom: `2px solid ${palette.colorGrayLighter}`,
          minHeight: '40px',
          padding: '0 12px',

          '&.Mui-selected': {
            color: `${palette.colorGray}!important`,
          },

          '.MuiTab-iconWrapper': {
            marginBottom: 0,
          },
        },
      },
    },

    MuiInput: {
      styleOverrides: {
        underline: {
          '&:before': {
            borderBottomColor: palette.colorGrayLighter,
          },
        },
        input: {
          lineHeight: '19px',
          height: '19px',
          padding: '6px 0 7px',
          '&::placeholder': {
            color: '#999',
            opacity: 1,
          },
          '&$disabled': {
            cursor: 'auto',
            opacity: 0.4,
          },
        },
      },
    },

    // Input
    MuiInputBase: {
      styleOverrides: {
        root: {
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
          padding: 0,
        },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        filled: {
          color: '#999',
        },
        shrink: {
          fontSize: '1.8rem',
        },
      },
    },

    MuiFormControlLabel: {
      styleOverrides: {
        label: {
          padding: 0,
          lineHeight: '2.5rem',
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 26,
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
          color: '#999',
          WebkitFontSmoothing: 'antialiased',
          '&.MuiFormLabel-filled': {
            color: '#999',
            WebkitFontSmoothing: 'antialiased',
          },
        },
      },
    },

    // Tooltip
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#FFFFFF',
          color: '#606060',
          fontSize: '1.4rem',
          border: 'none',
          boxShadow: '0 6px 18px black',
          borderRadius: 5,
          padding: 15,
          maxWidth: 350,
          fontWeight: 400,
          WebkitFontSmoothing: 'antialiased',
          lineHeight: 'normal',
        },
      },
    },

    MuiInputAdornment: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },

    // Slide
    MuiSlider: {
      styleOverrides: {
        root: {
          width: 'calc(100% - 16px)',
        },
        thumb: {
          width: 16,
          height: 16,
          color: palette.colorGrayLighter,
          marginTop: -6,

          '&::after': {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
        },
        rail: {
          height: 3,
          color: palette.colorGray,
          opacity: 0.85,
        },
        track: {
          height: 3,
          color: palette.colorGray,
        },
      },
    },

    // Switch
    MuiSwitch: {
      styleOverrides: {
        track: {
          backgroundColor: palette.colorGrayLighter,
          opacity: 1,
        },
      },
    },

    // Select
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: 'auto',
          '&:focus': {
            backgroundColor: 'transparent', // for consistency purposes
          },
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#787878',
        },
        colorPrimary: {
          color: '#787878',
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#787878',
        },
        colorPrimary: {
          color: '#787878',
        },
      },
    },

    // Helper text
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: palette.colorGray,
        },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        root: {
          overflow: 'hidden',
        },
        option: {
          '&[aria-selected="true"]': {
            backgroundColor: `${palette.colorGrayLightest} !important`,
            color: `${palette.primary.main} !important`,
            fontWeight: 'bold',
            paddingRight: '32px',
            // Check-mark icon.
            '&:after': {
              content: '""',
              display: 'inline-block',
              width: '20px',
              height: '20px',
              backgroundImage: 'url("/icons/icn-check.svg")', // Replace with the actual path to your image
              backgroundSize: 'cover',
              position: 'absolute',
              right: '8px', // Adjust as needed
            },
          },
        },
      },
    },
  },
});

export default defaultTheme;
